/**
 * referralPending — the referral link a person opened, kept on their device until their new account takes it (the
 * referral program, docs/PLAN-referral-program-20261019.md). The referral twin of ./pendingInvite.ts.
 *
 * The invitation page (/referrals/join/CODE and its short link /r/CODE) records the code the moment it opens. While it
 * is recorded:
 *  - signing up counts as an invitation flow (./inviteFlow.ts), so the account is not given the codeless trial before
 *    its referral start can be claimed;
 *  - PendingReferralGate brings a signed-in person back to the invitation page from the app, where the claim runs and
 *    the record is cleared on every outcome — so nothing is lost when someone signs up from another page, or closes
 *    the browser half way.
 * localStorage, not sessionStorage: a link is often opened days before the sign-up, and sign-up may happen in another
 * tab. Kept REFERRAL_ATTRIBUTION_DAYS, the window the program's terms state.
 */
import { REFERRAL_ATTRIBUTION_DAYS, normalizeReferralCode } from '../constants/referralVocabulary';

const KEY = 'prest0_pending_referral';
const MAX_AGE_MS = REFERRAL_ATTRIBUTION_DAYS * 24 * 60 * 60 * 1000;

export interface PendingReferral {
  code: string;
  savedAtMs: number;
}

/** The invitation page for a code. */
export function referralLandingPath(code: string): string {
  return `/referrals/join/${code}`;
}

/** True for the invitation page's own addresses (the long and the short link). */
export function isReferralLandingPath(pathname: string): boolean {
  const path = (pathname || '').toLowerCase();
  return path.startsWith('/referrals/join/') || path.startsWith('/r/');
}

export function savePendingReferral(rawCode: string, nowMs: number = Date.now()): void {
  const code = normalizeReferralCode(rawCode);
  if (!code) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, savedAtMs: nowMs } satisfies PendingReferral));
  } catch {
    /* storage off: the invitation page claims on the spot instead */
  }
}

export function getPendingReferral(nowMs: number = Date.now()): PendingReferral | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<PendingReferral>;
    const code = normalizeReferralCode(entry?.code);
    const savedAtMs = typeof entry?.savedAtMs === 'number' ? entry.savedAtMs : NaN;
    if (!code || !Number.isFinite(savedAtMs) || nowMs - savedAtMs > MAX_AGE_MS || savedAtMs - nowMs > 60_000) {
      localStorage.removeItem(KEY);
      return null;
    }
    return { code, savedAtMs };
  } catch {
    return null;
  }
}

export function clearPendingReferral(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
